import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Clock, ArrowLeft, ArrowRight, Building, User, Mail, Lock, Phone, 
  CheckCircle, Loader2, MapPin, Globe, FileText, Users, Briefcase,
  Image, HelpCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Industry options
const INDUSTRIES = [
  'Construction',
  'Healthcare',
  'Hospitality',
  'Manufacturing',
  'Retail',
  'Professional Services',
  'Transportation & Logistics',
  'Education',
  'Technology',
  'Real Estate',
  'Agriculture',
  'Other'
];

// Employee count ranges
const EMPLOYEE_COUNTS = [
  '1-5',
  '6-10',
  '11-25',
  '26-50',
  '51-100',
  '100+'
];

// Timezone options (common ones)
const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }
];

// How did you hear about us options
const REFERRAL_SOURCES = [
  'Google Search',
  'Social Media (Facebook, Instagram, LinkedIn)',
  'Friend or Colleague Referral',
  'Online Advertisement',
  'Industry Publication',
  'Trade Show / Event',
  'Other'
];

// Current time tracking methods
const CURRENT_SOLUTIONS = [
  'Paper timesheets',
  'Excel / Spreadsheets',
  'Another time tracking app',
  'No current system',
  'Other'
];

const TOTAL_STEPS = 5;

const SignUpWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    company_name: '',
    industry: '',
    employee_count: '',
    timezone: 'Australia/Sydney',
    
    // Step 2: Contact Details
    admin_name: '',
    admin_email: '',
    phone: '',
    website: '',
    
    // Step 3: Company Address
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Australia',
    
    // Step 4: Billing Info
    abn_tax_id: '',
    billing_email: '',
    billing_same_as_company: true,
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: 'Australia',
    
    // Step 5: Account & Quick Questions
    password: '',
    confirm_password: '',
    referral_source: '',
    current_solution: '',
    has_multiple_locations: false
  });

  const [errors, setErrors] = useState({});

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch(step) {
      case 1:
        if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
        if (!formData.industry) newErrors.industry = 'Please select an industry';
        if (!formData.employee_count) newErrors.employee_count = 'Please select employee count';
        if (!formData.timezone) newErrors.timezone = 'Please select a timezone';
        break;
      case 2:
        if (!formData.admin_name.trim()) newErrors.admin_name = 'Your name is required';
        if (!formData.admin_email.trim()) {
          newErrors.admin_email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
          newErrors.admin_email = 'Please enter a valid email';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        break;
      case 3:
        if (!formData.street_address.trim()) newErrors.street_address = 'Street address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State/Province is required';
        if (!formData.postal_code.trim()) newErrors.postal_code = 'Postal code is required';
        if (!formData.country.trim()) newErrors.country = 'Country is required';
        break;
      case 4:
        // Billing is optional, but if not same as company, validate billing address
        if (!formData.billing_same_as_company) {
          if (!formData.billing_street.trim()) newErrors.billing_street = 'Billing street is required';
          if (!formData.billing_city.trim()) newErrors.billing_city = 'Billing city is required';
        }
        break;
      case 5:
        if (!formData.password) {
          newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        }
        if (formData.password !== formData.confirm_password) {
          newErrors.confirm_password = 'Passwords do not match';
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsLoading(true);
    
    try {
      const payload = {
        // Basic Info
        company_name: formData.company_name.trim(),
        industry: formData.industry,
        employee_count: formData.employee_count,
        timezone: formData.timezone,
        
        // Contact
        admin_name: formData.admin_name.trim(),
        admin_email: formData.admin_email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        website: formData.website.trim() || null,
        
        // Address
        address: {
          street: formData.street_address.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          postal_code: formData.postal_code.trim(),
          country: formData.country.trim()
        },
        
        // Billing
        abn_tax_id: formData.abn_tax_id.trim() || null,
        billing_email: formData.billing_email.trim() || null,
        billing_address: formData.billing_same_as_company ? null : {
          street: formData.billing_street.trim(),
          city: formData.billing_city.trim(),
          state: formData.billing_state.trim(),
          postal_code: formData.billing_postal_code.trim(),
          country: formData.billing_country.trim()
        },
        
        // Account
        password: formData.password,
        
        // Marketing
        referral_source: formData.referral_source || null,
        current_solution: formData.current_solution || null,
        has_multiple_locations: formData.has_multiple_locations
      };
      
      await axios.post(`${API}/api/signup`, payload);
      
      setSubmittedEmail(formData.admin_email);
      setIsSuccess(true);
      toast.success('Sign up successful! Please check your email.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to sign up. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/signup/resend-verification`, null, {
        params: { email: submittedEmail }
      });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email.');
    } finally {
      setIsLoading(false);
    }
  };

  // Progress indicator
  const ProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        {[1, 2, 3, 4, 5].map((step) => (
          <div 
            key={step}
            className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-all ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-indigo-600 text-white ring-4 ring-indigo-200' 
                  : 'bg-slate-200 text-slate-500'
            }`}
          >
            {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
          </div>
        ))}
      </div>
      <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-300"
          style={{ width: `${((currentStep - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 text-xs text-slate-500">
        <span>Basic Info</span>
        <span>Contact</span>
        <span>Address</span>
        <span>Billing</span>
        <span>Account</span>
      </div>
    </div>
  );

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="signup-success-card">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription className="text-base">
              We've sent a verification link to<br />
              <span className="font-medium text-slate-900">{submittedEmail}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Click the link in your email to verify your account and complete the setup.
                The link will expire in 24 hours.
              </p>
            </div>
            
            <div className="text-center space-y-3 pt-4">
              <p className="text-sm text-slate-500">Didn't receive the email?</p>
              <Button 
                variant="outline" 
                onClick={handleResendVerification}
                disabled={isLoading}
                data-testid="resend-verification-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </Button>
            </div>
            
            <div className="pt-4 border-t">
              <Link to="/login">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="company_name"
                  placeholder="Acme Construction Pty Ltd"
                  className={`pl-10 ${errors.company_name ? 'border-red-500' : ''}`}
                  value={formData.company_name}
                  onChange={(e) => updateFormData('company_name', e.target.value)}
                  data-testid="signup-company-name"
                />
              </div>
              {errors.company_name && <p className="text-sm text-red-500">{errors.company_name}</p>}
            </div>

            <div className="space-y-2">
              <Label>Industry / Type of Business *</Label>
              <Select value={formData.industry} onValueChange={(v) => updateFormData('industry', v)}>
                <SelectTrigger className={errors.industry ? 'border-red-500' : ''} data-testid="signup-industry">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(ind => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.industry && <p className="text-sm text-red-500">{errors.industry}</p>}
            </div>

            <div className="space-y-2">
              <Label>Number of Employees *</Label>
              <Select value={formData.employee_count} onValueChange={(v) => updateFormData('employee_count', v)}>
                <SelectTrigger className={errors.employee_count ? 'border-red-500' : ''} data-testid="signup-employee-count">
                  <SelectValue placeholder="Select employee count" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYEE_COUNTS.map(count => (
                    <SelectItem key={count} value={count}>{count} employees</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.employee_count && <p className="text-sm text-red-500">{errors.employee_count}</p>}
            </div>

            <div className="space-y-2">
              <Label>Timezone *</Label>
              <Select value={formData.timezone} onValueChange={(v) => updateFormData('timezone', v)}>
                <SelectTrigger className={errors.timezone ? 'border-red-500' : ''} data-testid="signup-timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.timezone && <p className="text-sm text-red-500">{errors.timezone}</p>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin_name">Your Name (Primary Contact) *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="admin_name"
                  placeholder="John Smith"
                  className={`pl-10 ${errors.admin_name ? 'border-red-500' : ''}`}
                  value={formData.admin_name}
                  onChange={(e) => updateFormData('admin_name', e.target.value)}
                  data-testid="signup-admin-name"
                />
              </div>
              {errors.admin_name && <p className="text-sm text-red-500">{errors.admin_name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_email">Email Address *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="admin_email"
                  type="email"
                  placeholder="john@acme.com.au"
                  className={`pl-10 ${errors.admin_email ? 'border-red-500' : ''}`}
                  value={formData.admin_email}
                  onChange={(e) => updateFormData('admin_email', e.target.value)}
                  data-testid="signup-email"
                />
              </div>
              {errors.admin_email && <p className="text-sm text-red-500">{errors.admin_email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+61 4XX XXX XXX"
                  className={`pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  data-testid="signup-phone"
                />
              </div>
              {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website (Optional)</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="website"
                  placeholder="https://www.acme.com.au"
                  className="pl-10"
                  value={formData.website}
                  onChange={(e) => updateFormData('website', e.target.value)}
                  data-testid="signup-website"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="street_address">Street Address *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="street_address"
                  placeholder="123 Business Street, Suite 100"
                  className={`pl-10 ${errors.street_address ? 'border-red-500' : ''}`}
                  value={formData.street_address}
                  onChange={(e) => updateFormData('street_address', e.target.value)}
                  data-testid="signup-street"
                />
              </div>
              {errors.street_address && <p className="text-sm text-red-500">{errors.street_address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="Sydney"
                  className={errors.city ? 'border-red-500' : ''}
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  data-testid="signup-city"
                />
                {errors.city && <p className="text-sm text-red-500">{errors.city}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="state">State / Province *</Label>
                <Input
                  id="state"
                  placeholder="NSW"
                  className={errors.state ? 'border-red-500' : ''}
                  value={formData.state}
                  onChange={(e) => updateFormData('state', e.target.value)}
                  data-testid="signup-state"
                />
                {errors.state && <p className="text-sm text-red-500">{errors.state}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code *</Label>
                <Input
                  id="postal_code"
                  placeholder="2000"
                  className={errors.postal_code ? 'border-red-500' : ''}
                  value={formData.postal_code}
                  onChange={(e) => updateFormData('postal_code', e.target.value)}
                  data-testid="signup-postal"
                />
                {errors.postal_code && <p className="text-sm text-red-500">{errors.postal_code}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input
                  id="country"
                  placeholder="Australia"
                  className={errors.country ? 'border-red-500' : ''}
                  value={formData.country}
                  onChange={(e) => updateFormData('country', e.target.value)}
                  data-testid="signup-country"
                />
                {errors.country && <p className="text-sm text-red-500">{errors.country}</p>}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="abn_tax_id">ABN / Tax ID (Optional)</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="abn_tax_id"
                  placeholder="XX XXX XXX XXX"
                  className="pl-10"
                  value={formData.abn_tax_id}
                  onChange={(e) => updateFormData('abn_tax_id', e.target.value)}
                  data-testid="signup-abn"
                />
              </div>
              <p className="text-xs text-slate-500">Required for tax invoices</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing_email">Billing Email (if different)</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="billing_email"
                  type="email"
                  placeholder="accounts@acme.com.au"
                  className="pl-10"
                  value={formData.billing_email}
                  onChange={(e) => updateFormData('billing_email', e.target.value)}
                  data-testid="signup-billing-email"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 py-2">
              <Checkbox 
                id="billing_same"
                checked={formData.billing_same_as_company}
                onCheckedChange={(checked) => updateFormData('billing_same_as_company', checked)}
                data-testid="signup-billing-same"
              />
              <Label htmlFor="billing_same" className="text-sm font-normal cursor-pointer">
                Billing address is the same as company address
              </Label>
            </div>

            {!formData.billing_same_as_company && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700">Billing Address</p>
                <div className="space-y-2">
                  <Input
                    placeholder="Street Address"
                    className={errors.billing_street ? 'border-red-500' : ''}
                    value={formData.billing_street}
                    onChange={(e) => updateFormData('billing_street', e.target.value)}
                  />
                  {errors.billing_street && <p className="text-sm text-red-500">{errors.billing_street}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="City"
                    className={errors.billing_city ? 'border-red-500' : ''}
                    value={formData.billing_city}
                    onChange={(e) => updateFormData('billing_city', e.target.value)}
                  />
                  <Input
                    placeholder="State"
                    value={formData.billing_state}
                    onChange={(e) => updateFormData('billing_state', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Postal Code"
                    value={formData.billing_postal_code}
                    onChange={(e) => updateFormData('billing_postal_code', e.target.value)}
                  />
                  <Input
                    placeholder="Country"
                    value={formData.billing_country}
                    onChange={(e) => updateFormData('billing_country', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 6 characters"
                  className={`pl-10 ${errors.password ? 'border-red-500' : ''}`}
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  data-testid="signup-password"
                />
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="Confirm your password"
                  className={`pl-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                  value={formData.confirm_password}
                  onChange={(e) => updateFormData('confirm_password', e.target.value)}
                  data-testid="signup-confirm-password"
                />
              </div>
              {errors.confirm_password && <p className="text-sm text-red-500">{errors.confirm_password}</p>}
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-3">Quick Questions (Optional)</p>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>How did you hear about us?</Label>
                  <Select value={formData.referral_source} onValueChange={(v) => updateFormData('referral_source', v)}>
                    <SelectTrigger data-testid="signup-referral">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {REFERRAL_SOURCES.map(src => (
                        <SelectItem key={src} value={src}>{src}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Current time-tracking method?</Label>
                  <Select value={formData.current_solution} onValueChange={(v) => updateFormData('current_solution', v)}>
                    <SelectTrigger data-testid="signup-current-solution">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENT_SOLUTIONS.map(sol => (
                        <SelectItem key={sol} value={sol}>{sol}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="multiple_locations"
                    checked={formData.has_multiple_locations}
                    onCheckedChange={(checked) => updateFormData('has_multiple_locations', checked)}
                    data-testid="signup-multiple-locations"
                  />
                  <Label htmlFor="multiple_locations" className="text-sm font-normal cursor-pointer">
                    We have multiple work locations
                  </Label>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-50 rounded-lg p-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Your Plan</p>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-lg font-semibold text-slate-900">$7/month</p>
                  <p className="text-xs text-slate-500">All features included • Unlimited users</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-green-600 font-medium">14-day free trial</p>
                  <p className="text-xs text-slate-500">No credit card required</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return { title: 'Company Information', desc: 'Tell us about your business' };
      case 2: return { title: 'Contact Details', desc: 'How can we reach you?' };
      case 3: return { title: 'Company Address', desc: 'Where is your business located?' };
      case 4: return { title: 'Billing Information', desc: 'For invoicing purposes (optional)' };
      case 5: return { title: 'Create Your Account', desc: 'Almost done!' };
      default: return { title: '', desc: '' };
    }
  };

  const stepInfo = getStepTitle();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">ClockIn Pro</span>
          </Link>
        </div>

        <Card data-testid="signup-wizard-card">
          <CardHeader className="pb-4">
            <ProgressBar />
            <CardTitle>{stepInfo.title}</CardTitle>
            <CardDescription>{stepInfo.desc}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1}
                data-testid="signup-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {currentStep < TOTAL_STEPS ? (
                <Button 
                  onClick={handleNext}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="signup-next-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="signup-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Setup
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Sign In Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                Already have an account?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-4">
          <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUpWizard;
